import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, switchMap } from 'rxjs';
import { ResponseApiHelper } from 'src/app/helpers/response-api/response-api-helper';
import { HandleErrorService } from 'src/app/services/handle-error/handle-error.service';
import { CarbonModalService } from 'src/app/services/modal/carbon-modal.service';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';
import { Pagination } from 'src/app/shared/interfaces/pagination';
import { AppConfigService } from 'src/environments/app-config.service';
import { RegisterService } from '../../services/register.service';

import { CarbonFilter } from './../interfaces/carbon-filter';
import { CharacteristicConsultRequestOptions } from '../shared/characteristic-consult-request.options';
import {
  extractPrintResults,
  isEmptyValue,
  toConsultApiFilter,
} from '../shared/characteristic-consult.utils';

@Injectable({
  providedIn: 'root'
})
export class CharacteristicConsultService extends ResponseApiHelper<CarbonCredit> {
  private static readonly EXPORT_PAGE_SIZE = 500;

  private environment: any;
  private readonly apiUrl: string;

  filters: BehaviorSubject<CarbonFilter> = new BehaviorSubject({} as CarbonFilter);

  constructor(
    protected override httpClient: HttpClient,
    private appConfig: AppConfigService,
    private carbonModalService: CarbonModalService,
    private registerService: RegisterService,
    private handleErrorService: HandleErrorService
  ) {
    super(httpClient);

    this.environment = this.appConfig.config;
    this.apiUrl = this.environment.apiIssuer;
  }

  getCarbonCredit(
    filters: CarbonFilter,
    pagination: Pagination<CarbonCredit>,
    options?: CharacteristicConsultRequestOptions,
  ): Observable<Pagination<CarbonCredit>> {
    return this.fetchInstrumentPage(
      '/instruments/approved-instruments',
      'approvedInstruments',
      filters,
      pagination,
      options,
    );
  }

  getCarbonCreditPrint(
    filters: CarbonFilter,
    options?: CharacteristicConsultRequestOptions,
  ): Observable<CarbonCredit[]> {
    const url = `${this.apiUrl}/instruments/approved-instruments/print`;
    const params = this.registerService.carbonPrintParams(toConsultApiFilter(filters));

    return this.httpClient.get<CarbonCredit[] | { results?: CarbonCredit[] }>(url, { params }).pipe(
      map((response) => {
        const items = extractPrintResults(response) as CarbonCredit[];
        return this.registerService.transformDataCarbonCredit(items);
      }),
      catchError((err) => this.handleConsultError(err, options)),
    );
  }

  queryCredits(
    filters: CarbonFilter,
    pagination: Pagination<CarbonCredit>,
    options?: CharacteristicConsultRequestOptions,
  ): Observable<{ results: CarbonCredit[]; total: number; totalPages?: number }> {
    if (!isEmptyValue(filters.carbonCreditCode)) {
      return this.queryByCarbonCreditCode(filters.carbonCreditCode!.trim(), options);
    }

    return this.getCarbonCredit(filters, pagination, options).pipe(
      map(response => ({
        results: response.results ?? [],
        total: response.total ?? 0,
        totalPages: response.totalPages,
      })),
    );
  }

  exportCredits(
    filters: CarbonFilter,
    options?: CharacteristicConsultRequestOptions,
  ): Observable<CarbonCredit[]> {
    if (!isEmptyValue(filters.carbonCreditCode)) {
      return this.queryByCarbonCreditCode(filters.carbonCreditCode!.trim(), options).pipe(
        map(response => response.results),
      );
    }

    const requestOptions = {
      suppressLegacyErrorModal: true,
      ...options,
    };

    return this.getCarbonCreditPrint(filters, requestOptions).pipe(
      switchMap((rows) => rows.length > 0
        ? of(rows)
        : this.fetchAllApprovedInstruments(filters, options)),
      catchError(() => this.fetchAllApprovedInstruments(filters, options)),
    );
  }

  getCarbonCreditById(
    code: string,
    options?: CharacteristicConsultRequestOptions,
  ): Observable<CarbonCredit> {
    const url = `${this.apiUrl}/instruments/${code}`;
    return this.httpClient.get<{ data: CarbonCredit }>(url).pipe(
      map(response => this.registerService.transformCarbonCredit(response.data)),
      catchError((err) => this.handleConsultError(err, options)),
    );
  }

  getBeneficiaries(operationId: number): Observable<{ data?: unknown[] }> {
    const url = `${this.environment.apiOperations}/beneficiaries/${operationId}/operations`;
    return this.httpClient.get<{ data?: unknown[] }>(url);
  }

  private queryByCarbonCreditCode(
    code: string,
    options?: CharacteristicConsultRequestOptions,
  ): Observable<{ results: CarbonCredit[]; total: number }> {
    return this.getCarbonCreditById(code, options).pipe(
      map(credit => ({
        results: [credit],
        total: 1,
      })),
      catchError(() => of({ results: [], total: 0 })),
    );
  }

  private fetchInstrumentPage(
    endpoint: string,
    responseKey: string,
    filters: CarbonFilter,
    pagination: Pagination<CarbonCredit>,
    options?: CharacteristicConsultRequestOptions,
  ): Observable<Pagination<CarbonCredit>> {
    const url = `${this.apiUrl}${endpoint}`;
    const params = this.registerService.carbonFilterParams(toConsultApiFilter(filters), pagination);

    return this.genericGet(url, responseKey, params, pagination).pipe(
      map((response) => {
        response.results = this.registerService.transformDataCarbonCredit(response.results);
        return response;
      }),
      catchError((err) => this.handleConsultError(err, options)),
    );
  }

  private fetchAllApprovedInstruments(
    filters: CarbonFilter,
    options?: CharacteristicConsultRequestOptions,
    page = 1,
    accumulated: CarbonCredit[] = [],
  ): Observable<CarbonCredit[]> {
    const pagination: Pagination<CarbonCredit> = {
      page,
      size: CharacteristicConsultService.EXPORT_PAGE_SIZE,
      results: [],
    };

    return this.getCarbonCredit(filters, pagination, options).pipe(
      switchMap((response) => {
        const batch = response.results ?? [];
        const pageSize = pagination.size ?? CharacteristicConsultService.EXPORT_PAGE_SIZE;
        const totalPages = response.totalPages
          ?? Math.ceil((response.total ?? 0) / pageSize);
        const merged = [...accumulated, ...batch];

        if (!batch.length || page >= totalPages) {
          return of(merged);
        }

        return this.fetchAllApprovedInstruments(filters, options, page + 1, merged);
      }),
    );
  }

  private handleConsultError(
    err: unknown,
    options?: CharacteristicConsultRequestOptions,
  ) {
    if (!options?.suppressLegacyErrorModal) {
      this.carbonModalService.openCarbonCreditGetListError();
    }

    return this.handleErrorService.handleError(err as never);
  }
}
